/**
 * Sticky page header for the Accounting Rules page.
 *
 * Rendered via AppLayout's pageHeader slot (same pattern as
 * AccountingOverviewHeader, ChartOfAccountsHeader, etc.), so the
 * header stays pinned at the top while the content below scrolls.
 * See authedRoutes.tsx `pageHeaderForPath` for the wiring.
 *
 * Layout: breadcrumb + title + subtitle on the left, three
 * right-side actions:
 *   - "Learn more" ghost link (teal, no border)
 *   - "+ Import rules" ghost button
 *   - "+ New rule" primary filled dark button
 *
 * Mobile: subtitle hidden, breadcrumb and title stay, actions
 * collapse to a single compact "+ New" primary button.
 *
 * For PR 9, Import rules and New rule stub with a toast message
 * ("Rule builder coming next PR"). The toast is self-contained
 * so the header stays decoupled from the main page body.
 */

import { useEffect, useState, type CSSProperties } from "react";
import { useBreakpoint } from "../../../../hooks/useBreakpoint";

const PALETTE = {
  ink: "#0E1A1A",
  ink2: "#263238",
  ink3: "#3A4A52",
  ink4: "#6B7684",
  line: "#D8DEE6",
  tealDark: "#0F6E56",
  bgFade: "#F3F5F5",
};

const TOAST_DURATION_MS = 3200;
const LEARN_MORE_URL = "https://help.novala.app/accounting/rules";

export function RulesHeader() {
  const { isMobile } = useBreakpoint();
  const [toast, setToast] = useState<string | null>(null);

  useEffect(() => {
    if (!toast) return;
    const t = window.setTimeout(() => setToast(null), TOAST_DURATION_MS);
    return () => window.clearTimeout(t);
  }, [toast]);

  const handleImport = () => {
    setToast("Rule import is coming in the next release.");
  };

  const handleNewRule = () => {
    setToast("Rule builder is coming in the next release.");
  };

  return (
    <div style={isMobile ? wrapStyleMobile : wrapStyle}>
      <div style={leftColStyle}>
        <div style={breadcrumbStyle}>
          <span>Accounting</span>
          <span style={breadcrumbSepStyle}>/</span>
          <span style={breadcrumbCurrentStyle}>Rules</span>
        </div>
        <h1 style={isMobile ? titleStyleMobile : titleStyle}>Rules</h1>
        {!isMobile && (
          <div style={subtitleStyle}>
            Teach Vela how to categorize your transactions automatically.
            Set a rule once; Vela applies it to every match forever.
          </div>
        )}
      </div>

      <div style={actionsStyle}>
        {!isMobile && (
          <>
            <a
              href={LEARN_MORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              style={learnLinkStyle}
            >
              Learn more
            </a>
            <button type="button" style={ghostBtnStyle} onClick={handleImport}>
              Import rules
            </button>
          </>
        )}
        <button type="button" style={primaryBtnStyle} onClick={handleNewRule}>
          + {isMobile ? "New" : "New rule"}
        </button>
      </div>

      {toast && <Toast message={toast} />}
    </div>
  );
}

function Toast({ message }: { message: string }) {
  return (
    <div style={toastStyle} role="status" aria-live="polite">
      {message}
    </div>
  );
}

const wrapStyle: CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-end",
  gap: 16,
  flexWrap: "wrap",
  padding: "20px 32px 14px",
  background: "#ffffff",
  width: "100%",
};

const wrapStyleMobile: CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-end",
  gap: 10,
  padding: "14px 16px 10px",
  background: "#ffffff",
  width: "100%",
};

const leftColStyle: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  minWidth: 0,
};

const breadcrumbStyle: CSSProperties = {
  fontSize: 12,
  color: PALETTE.ink3,
  fontWeight: 500,
  marginBottom: 3,
};

const breadcrumbSepStyle: CSSProperties = {
  color: PALETTE.ink4,
  margin: "0 5px",
};

const breadcrumbCurrentStyle: CSSProperties = {
  color: PALETTE.ink,
  fontWeight: 600,
};

const titleStyle: CSSProperties = {
  fontSize: 22,
  fontWeight: 800,
  margin: 0,
  color: PALETTE.ink,
  letterSpacing: "-0.015em",
  lineHeight: 1.15,
};

const titleStyleMobile: CSSProperties = {
  ...titleStyle,
  fontSize: 19,
};

const subtitleStyle: CSSProperties = {
  fontSize: 12.5,
  color: PALETTE.ink3,
  marginTop: 3,
  fontWeight: 500,
  maxWidth: 640,
};

const actionsStyle: CSSProperties = {
  display: "flex",
  gap: 8,
  alignItems: "center",
  flexWrap: "wrap",
  flexShrink: 0,
};

const learnLinkStyle: CSSProperties = {
  background: "transparent",
  border: 0,
  color: PALETTE.tealDark,
  fontSize: 12.5,
  fontWeight: 600,
  cursor: "pointer",
  padding: "8px 4px",
  textDecoration: "none",
  fontFamily: "inherit",
};

const ghostBtnStyle: CSSProperties = {
  background: "transparent",
  border: `1px solid ${PALETTE.line}`,
  color: PALETTE.ink2,
  fontSize: 12.5,
  fontWeight: 500,
  padding: "8px 12px",
  borderRadius: 8,
  cursor: "pointer",
  fontFamily: "inherit",
  display: "inline-flex",
  alignItems: "center",
  gap: 5,
};

const primaryBtnStyle: CSSProperties = {
  background: PALETTE.ink,
  color: "#ffffff",
  fontSize: 12.5,
  fontWeight: 600,
  padding: "9px 14px",
  borderRadius: 8,
  cursor: "pointer",
  border: 0,
  display: "inline-flex",
  alignItems: "center",
  gap: 5,
  boxShadow: "0 1px 2px rgba(14, 26, 26, 0.08)",
  fontFamily: "inherit",
};

const toastStyle: CSSProperties = {
  position: "fixed",
  bottom: 24,
  left: "50%",
  transform: "translateX(-50%)",
  background: PALETTE.ink,
  color: "#ffffff",
  padding: "10px 16px",
  borderRadius: 8,
  fontSize: 13,
  fontWeight: 500,
  boxShadow: "0 8px 20px -4px rgba(14, 26, 26, 0.28)",
  zIndex: 1100,
};